<template>
  <div class="space-y-1">
    <span class="block text-xs font-semibold uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">{{
      label
    }}</span>

    <div class="flex gap-1.5">
      <select
        :value="anchorType"
        class="rounded-xl border border-gray-200 bg-white px-2 py-2 text-sm text-gray-900 shadow-sm outline-none transition focus:border-sky-400 dark:border-gray-700 dark:bg-gray-900 dark:text-white"
        @change="onTypeChange">
        <option v-for="type in TIME_ANCHOR_TYPES" :key="type.value" :value="type.value">
          {{ tt(type.label.en, type.label.sl) }}
        </option>
      </select>

      <input
        v-if="anchorType === 'time'"
        :value="timeValue"
        type="time"
        class="min-w-0 flex-1 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm outline-none transition focus:border-sky-400 dark:border-gray-700 dark:bg-gray-900 dark:text-white"
        @input="onTimeChange" />

      <div
        v-else
        class="flex min-w-0 flex-1 items-center gap-1 rounded-xl border border-gray-200 bg-white px-2 py-2 shadow-sm dark:border-gray-700 dark:bg-gray-900">
        <span class="text-sm text-gray-400">±</span>
        <input
          :value="offsetMinutes"
          type="number"
          min="-720"
          max="720"
          class="w-full min-w-0 bg-transparent text-sm text-gray-900 outline-none dark:text-white"
          @input="onOffsetChange" />
        <span class="text-xs text-gray-400">min</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import {
  TIME_ANCHOR_TYPES,
  defaultTimeAnchor,
  normalizeAnchor,
  type TimeSwitchTime,
} from '../../utils/switchAutomation';
import { tt } from '../../utils/consoleText';

interface Props {
  label: string;
  modelValue: TimeSwitchTime;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  'update:modelValue': [value: TimeSwitchTime];
}>();

const anchor = computed(() => normalizeAnchor(props.modelValue, '08:00'));
const anchorType = computed(() => anchor.value.type);
const timeValue = computed(() => (anchor.value.type === 'time' ? (anchor.value.time ?? '08:00') : '08:00'));
const offsetMinutes = computed(() => (anchor.value.type === 'time' ? 0 : (anchor.value.offset_minutes ?? 0)));

function onTypeChange(event: Event) {
  const type = (event.target as HTMLSelectElement).value as 'time' | 'sunrise' | 'sunset';
  emit('update:modelValue', defaultTimeAnchor(type));
}

function onTimeChange(event: Event) {
  const value = (event.target as HTMLInputElement).value;
  emit('update:modelValue', { type: 'time', time: value || '08:00' });
}

function onOffsetChange(event: Event) {
  const value = Number((event.target as HTMLInputElement).value);
  emit('update:modelValue', {
    type: anchorType.value === 'time' ? 'time' : anchorType.value,
    offset_minutes: Number.isFinite(value) ? value : 0,
  });
}
</script>
